import React, { useMemo } from "react";
import { Pill, Text } from "@foris/avocado-suite";
import { Classroom, GroupScheduleSummary, Instructor } from "@models/ISchema";
import styles from "./groupDetail.module.scss";
import { useTranslation } from "react-i18next";

interface Props {
  schedules: GroupScheduleSummary[];
  primaryInstructorId: string;
}

interface IStringifiedSchedule {
  days: string;
  hourTime: string;
  classrooms: string;
  dateRange: string;
}

const ScheduleSummary = ({ schedules, primaryInstructorId }: Props) => {
  const { t } = useTranslation();

  // for every schedule, sort the instructors by `primaryInstructorId` first
  const instructorsBySchedule = (schedules ?? []).map(schedule => {
    const instructors = schedule?.instructors ?? [];
    return instructors.sort((a: Instructor, b: Instructor) => {
      if (a?.id === primaryInstructorId) return -1;
      if (b?.id === primaryInstructorId) return 1;
      return 0;
    });
  });

  const groupedSchedules = useMemo(() => {
    const stringifySchedules: IStringifiedSchedule[] = schedules?.map(
      (schedule: GroupScheduleSummary) => {
        const days = schedule?.days
          ?.map(day => day.code)
          ?.filter(code => !!code)
          ?.join(", ");
        const hourTime = schedule?.schedule;
        const dateRange = schedule?.dateRange;
        const classrooms = schedule?.classrooms
          ?.map((classroom: Classroom) => {
            const building = classroom?.building?.code;
            const campus = classroom?.building?.campus?.code;
            const capacity = classroom?.capacity;

            if (!building && !campus && !classroom?.code) {
              return null;
            }

            return `${classroom?.code || "-"} | ${building || "-"} | ${campus ||
              "-"} (${capacity})`;
          })
          ?.filter(classroom => !!classroom)
          ?.join(", ");

        return { days, hourTime, classrooms, dateRange };
      },
    );

    // group schedules by `datetime`, `classrooms` and `instructors`
    const newGroupedSchedules: Record<string, IStringifiedSchedule[]> = {};

    (stringifySchedules ?? []).forEach((schedule: IStringifiedSchedule) => {
      const key = `${schedule?.days}${schedule?.hourTime}-${schedule?.classrooms}`;
      if (!newGroupedSchedules[key]) {
        newGroupedSchedules[key] = [];
      }
      newGroupedSchedules[key].push(schedule);
    });

    if (!Object.keys(newGroupedSchedules).length) {
      return {
        empty: [
          {
            days: null,
            hourTime: null,
            classrooms: null,
            dateRange: null,
          },
        ],
      };
    }

    return newGroupedSchedules ?? {};
  }, [schedules]);

  return (
    <>
      {Object.entries(groupedSchedules)?.map(([key, stringifySchedules], idx) => {
        const days = stringifySchedules[0]?.days;
        const hourTime = stringifySchedules[0]?.hourTime;
        const filteredSchedules = stringifySchedules?.filter(schedule => !!schedule?.dateRange);
        const dateRange = filteredSchedules?.length
          ? filteredSchedules.map(schedule => schedule?.dateRange).join(" | ")
          : null;
        const classrooms = stringifySchedules[0]?.classrooms;

        return (
          <ul key={key} className={styles.contentDetails}>
            <li className={styles.contentDetails_item}>
              <Text className={styles.itemTitle} type="sm" weight="medium">
                {t("groups-manager.group-details.columns.term")}
              </Text>

              <Text className={styles.itemLabel}>
                {dateRange || t("groups-manager.group-details.columns.without-term")}
              </Text>
            </li>

            <li className={styles.contentDetails_item}>
              <Text className={styles.itemTitle} type="sm" weight="medium">
                {t("groups-manager.group-details.columns.schedule")}
              </Text>

              <Text className={styles.itemLabel}>
                {days && hourTime
                  ? `${days} ${hourTime}`
                  : t("groups-manager.group-details.columns.without-schedules")}
              </Text>
            </li>

            <li className={styles.contentDetails_item}>
              <Text className={styles.itemTitle} type="sm" weight="medium">
                {t("groups-manager.group-details.columns.classrooms")}
              </Text>

              <Text className={styles.itemLabel}>
                {classrooms || t("groups-manager.group-details.columns.without-classrooms")}
              </Text>
            </li>

            <li className={styles.contentDetails_item}>
              <Text className={styles.itemTitle} type="sm" weight="medium">
                {t("groups-manager.group-details.columns.instructors")}
              </Text>

              {instructorsBySchedule[idx]?.length ? (
                <div className={styles.itemList}>
                  {instructorsBySchedule[idx]?.map((instructor: Instructor) => (
                    <Text key={`${idx}-${instructor?.id}`} className={styles.itemLabel}>
                      {instructor?.name ||
                        t("groups-manager.group-details.columns.without-instructors")}
                      {instructor?.id === primaryInstructorId && (
                        <Pill>{t("groups-manager.group-details.columns.principal")}</Pill>
                      )}
                    </Text>
                  ))}
                </div>
              ) : (
                <Text className={styles.itemLabel}>
                  {t("groups-manager.group-details.columns.without-instructors")}
                </Text>
              )}
            </li>
          </ul>
        );
      })}
    </>
  );
};

export default ScheduleSummary;
